





import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../context/ApiProvider';
import {
  startOfMonth, endOfMonth, addMonths, subMonths,
  startOfWeek, addDays, format, isSameMonth
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useModal } from '../context/ModalProvider';
import Lottie from 'lottie-react';
import { QRCodeSVG } from 'qrcode.react';
import discordAnim from '../assets/discord.json';

const FOOTER_ACCENT = '#E0A15E';
const FOOTER_ACCENT_GRADIENT = `linear-gradient(135deg, ${FOOTER_ACCENT} 0%, ${FOOTER_ACCENT} 100%)`;
const FOOTER_ACCENT_GLOW = 'rgba(224, 161, 94, 0.18)';

/* ─────────────────── animation presets ─────────────────── */
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

/* ─────────────────── styled components ─────────────────── */
const Footer = styled.footer`
  scroll-snap-align: start;
  background: #1a202c;
  color: #fff;
  padding: 4rem 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 50%, rgba(224, 161, 94, 0.12) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(224, 161, 94, 0.12) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1400px;
  position: relative;
  z-index: 1;
`;

const BookingSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  margin-bottom: 4rem;

  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
  }
`;

const BookingContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 639px) {
    align-items: center;
    text-align: center;
  }
`;

const BookingTitle = styled(motion.h2)`
  font-size: clamp(1.75rem, 4vw, 3rem);
  font-weight: 700;
  margin: 0 0 1rem;
  line-height: 1.2;

  span {
    font-style: italic;
    color: ${FOOTER_ACCENT};
  }
`;

const BookingSubtitle = styled(motion.p)`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 2rem;
  line-height: 1.6;

  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
`;

const BookingFeatures = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 639px) {
    width: fit-content;
    align-self: center;
    align-items: flex-start;
  }
`;

const BookingFeature = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);

  &::before {
    content: '✓';
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${FOOTER_ACCENT_GRADIENT};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.8rem;
    flex-shrink: 0;
  }

  @media (max-width: 639px) {
    justify-content: flex-start;
    text-align: left;
  }
`;

const CalendarContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 1.5rem;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;

  @media (min-width: 640px) {
    padding: 2rem;
    max-width: 500px;
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const MonthYear = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  flex: 1;
  text-align: center;

  @media (min-width: 640px) {
    font-size: 1.25rem;
  }
`;

const NavButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background 0.2s ease;
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 640px) {
    font-size: 1.5rem;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
  margin-bottom: 1.5rem;

  @media (min-width: 640px) {
    gap: 0.5rem;
  }
`;

const DayHeader = styled.div`
  text-align: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  padding: 0.5rem 0.25rem;
  background: transparent;
  border: none;

  @media (min-width: 640px) {
    font-size: 0.8rem;
    padding: 0.5rem;
  }
`;

const DayCell = styled.button<{ isToday?: boolean; isSelected?: boolean; isAvailable?: boolean; isEmpty?: boolean }>`
  aspect-ratio: 1;
  border: none;
  border-radius: 6px;
  background: ${props => {
    if (props.isEmpty) return 'transparent';
    if (props.isSelected) return FOOTER_ACCENT_GRADIENT;
    if (props.isAvailable) return 'rgba(255, 255, 255, 0.1)';
    return 'transparent';
  }};
  color: ${props => {
    if (props.isEmpty) return 'transparent';
    if (props.isAvailable) return '#fff';
    return 'rgba(255, 255, 255, 0.3)';
  }};
  cursor: ${props => props.isAvailable ? 'pointer' : 'default'};
  font-size: 0.8rem;
  font-weight: ${props => props.isToday ? '700' : '500'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 35px;

  @media (min-width: 640px) {
    border-radius: 8px;
    font-size: 0.9rem;
    min-height: auto;
  }

  &:hover {
    background: ${props => {
    if (props.isEmpty || !props.isAvailable) return 'transparent';
    if (props.isAvailable && !props.isSelected) {
      return 'rgba(255, 255, 255, 0.2)';
    }
    return props.isSelected ? FOOTER_ACCENT_GRADIENT :
      'rgba(255, 255, 255, 0.1)';
  }};
    transform: ${props => props.isAvailable && !props.isEmpty ? 'scale(1.05)' : 'none'};
  }
`;



const BookButton = styled.button`
  width: 100%;
  padding: 1rem;
  margin-top: 1.5rem;
  background: ${FOOTER_ACCENT_GRADIENT};
  border: none;
  border-radius: 12px;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px ${FOOTER_ACCENT_GLOW};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Grid = styled(motion.div)`
  width: 100%;
  max-width: 1200px;
  display: grid;
  gap: 2rem;

  @media (max-width: 639px) {
    justify-items: center;

    & > * {
      display: grid;
      justify-items: center;
      text-align: center;
    }
  }

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 900px) {
    grid-template-columns: repeat(4, 1fr);
  }

  h4 {
    margin: 0 0 1rem;
    font-size: 1.125rem;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: 0.95rem;

    li + li { margin-top: .5rem; }
    a { color: inherit; text-decoration: none; opacity: 0.85; }
    a:hover { opacity: 1; }
  }
`;

const Copy = styled.p`
  font-size: 0.8rem;
  opacity: 0.75;
  margin: 0;
  text-align: center;
`;

const ContactPhoneLink = styled.a`
  && {
    color: #e0a15e;
    opacity: 1;
    text-decoration: none;
  }

  &&:hover {
    color: #e0a15e;
    opacity: 1;
    text-decoration: underline;
  }
`;

/* ─────────────────── component ─────────────────── */
export default function BookingFooterSection() {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const { open } = useModal();
  const [showDiscord, setShowDiscord] = useState(true); 
  const DISCORD_INVITE =
    process.env.REACT_APP_PUBLIC_DISCORD_INVITE_URL || 'https://discord.gg/VWcB5YVDZk';

  const daysOfWeek = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const bookingFeatures = [
    "Quick 15-minute guided tour",
    "See if we're the right fit",
    "No sales pressure",
    "Free consultation"
  ];


  const DiscordContainer = styled(CalendarContainer)`
  /* centre everything instead of the calendar’s default left‑flow */
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  /* optional breathing space between stacked elements */
  gap: 1.25rem;
`;



  const QRWrapper = styled.div`
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
`;

const ToggleLink = styled.button`
  background: none;
  border: none;
  margin-top: 1rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  text-decoration: underline;
  cursor: pointer;
`;




  const api = useApi();
  const startDate = format(month, 'yyyy-MM-01');
  const endDate = format(endOfMonth(month), 'yyyy-MM-dd');
  const viewerTimezone = React.useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  );
  const formatUtcInViewerTimezone = React.useCallback(
    (utcIso: string, pattern: string) =>
      format(toZonedTime(new Date(utcIso), viewerTimezone), pattern),
    [viewerTimezone],
  );

  const { data: slots = [], isLoading } = useQuery({
    queryKey: ['public-availability', startDate, endDate, viewerTimezone],
    queryFn: () =>
      api.get('/public/availability', {
        params: { start: startDate, end: endDate, timezone: viewerTimezone },
      })
        .then(r => r.data),
    staleTime: 60_000,
  });

  // group the slots by viewer-local date -> Set<utcDateTime>
  const slotsByDate = React.useMemo(() => {
    const map: Record<string, string[]> = {};
    slots.forEach((s: any) => {
      const iso = s.utcDateTime as string;
      const date = formatUtcInViewerTimezone(iso, 'yyyy-MM-dd');
      (map[date] ||= []).push(iso);
    });
    return map;
  }, [formatUtcInViewerTimezone, slots]);

  const gridStart = startOfWeek(month, { weekStartsOn: 0 }); // Sun
  const calendarDays: Date[] = [];
  for (let i = 0; i < 42; i++) calendarDays.push(addDays(gridStart, i));


  const hasAvailability = (d: Date) =>
    Boolean(slotsByDate[format(d, 'yyyy-MM-dd')]);

  const timeSlotsForDay = (d: Date) => {
    const key = format(d, 'yyyy-MM-dd');
    return (slotsByDate[key] || [])
      .map(t => formatUtcInViewerTimezone(t, 'h:mmaaa').toLowerCase());
  };



  const handleDateSelect = (d: Date) => {
    if (!hasAvailability(d)) return;

    /* feed the modal the day & its times */
    open('public-timeslot', {
      date: d,
      times: timeSlotsForDay(d),

      /* modal calls this when user chooses a slot */
      onSelect: (time: string) => {
        setSelectedDate(d);
        setSelectedTime(time);
      },
    });
  };



  const handleBooking = () => {
    if (selectedDate && selectedTime) {
      alert(`Booking confirmed for ${format(selectedDate, 'MMMM d')} at ${selectedTime}!`);
    }
  };

  return (
    <Footer id="footer">
      <Container>
       <BookingSection
  variants={staggerContainer}
  initial="hidden"
  whileInView="visible"
  viewport={{ once: true, amount: 0.2 }}
>
  {/* ─────────────── LEFT COLUMN ─────────────── */}
  <BookingContent>
    <BookingTitle variants={fadeInUp}>
      See if Cybard is the right fit for you <span>(it totally is)</span>
    </BookingTitle>

    <BookingSubtitle variants={fadeInUp}>
      Schedule a quick, 15‑minute guided tour through our process and see how we can help grow your business.
    </BookingSubtitle>

    <BookingFeatures variants={fadeInUp}>
      {bookingFeatures.map((feature, i) => (
        <BookingFeature key={i} variants={fadeInUp}>
          {feature}
        </BookingFeature>
      ))}
    </BookingFeatures>
  </BookingContent>

  {/* ─────────────── RIGHT COLUMN (CONDITIONAL) ─────────────── */}
  {showDiscord ? (
    <DiscordContainer
    variants={fadeInUp}
    initial="hidden"
    animate="visible"  
  >
      <Lottie
        animationData={discordAnim}
        loop
        autoplay
        style={{ width: 120, height: 120, margin: '0 auto 1rem' }}
      />

      <MonthYear style={{ fontSize: '1.3rem' }}>
        Join our Discord Community
      </MonthYear>
      <BookingSubtitle style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        Get instant answers, drop requests, and connect with the team in real time.
      </BookingSubtitle>

      <BookButton
        as="a"
        href={DISCORD_INVITE}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open Discord
      </BookButton>

      <QRWrapper>
        <QRCodeSVG
          value={DISCORD_INVITE}
          size={128}
          fgColor="#ffffff"
          bgColor="transparent"
          level="H"
        />
      </QRWrapper>

      {/* <ToggleLink onClick={() => setShowDiscord(false)}>
        Prefer a meeting?
      </ToggleLink> */}
    </DiscordContainer>
  ) : (
    <CalendarContainer
    variants={fadeInUp}
    initial="hidden"
    animate="visible"   
  >
      <CalendarHeader>
        <NavButton onClick={() => setMonth(subMonths(month, 1))}>‹</NavButton>
        <MonthYear>{format(month, 'MMMM yyyy')}</MonthYear>
        <NavButton onClick={() => setMonth(addMonths(month, 1))}>›</NavButton>
      </CalendarHeader>

      <CalendarGrid>
        {daysOfWeek.map(d => (
          <DayHeader key={d}>{d}</DayHeader>
        ))}

        {calendarDays.map(d => {
          const inMonth = isSameMonth(d, month);
          return (
            <DayCell
              key={d.toISOString()}
              isEmpty={!inMonth}
              isAvailable={inMonth && hasAvailability(d)}
              isSelected={
                selectedDate
                  ? format(selectedDate, 'yyyy-MM-dd') === format(d, 'yyyy-MM-dd')
                  : false
              }
              isToday={format(d, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')}
              onClick={() => handleDateSelect(d)}
            >
              {inMonth ? format(d, 'd') : ''}
            </DayCell>
          );
        })}
      </CalendarGrid>

      <BookButton
        disabled={isLoading || !selectedDate || !selectedTime}
        onClick={handleBooking}
      >
        {selectedDate && selectedTime
          ? `Book call for ${format(selectedDate, 'MMMM d')} at ${selectedTime}`
          : 'Select date & time to book'}
      </BookButton>

      <ToggleLink onClick={() => setShowDiscord(true)}>
        Prefer Discord chat?
      </ToggleLink>
    </CalendarContainer>
  )}
</BookingSection>


        <Grid
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div variants={fadeInUp}>
            <h4>Cybard</h4>
            <p style={{ fontSize: '.95rem', lineHeight: 1.5 }}>
              Building, rebranding &amp; optimizing digital experiences for legendary businesses
            </p>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <h4>Company</h4>
            <ul>
              <li><a href="/services">Services</a></li>
              <li><a href="/services/managed-marketing">Managed Marketing</a></li>
              <li><a href="/services/assets-design">Assets &amp; Design</a></li>
              <li><a href="/services/platform-development">Platform Development</a></li>
            </ul>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <h4>Resources</h4>
            <ul>
              <li><a href="/publications">Publications</a></li>
              <li><a href="/capital-partner-network">Capital partner network</a></li>
              <li><a href="/case-studies">Case studies</a></li>
              <li><a href="/careers">Careers</a></li>
            </ul>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <h4>Contact</h4>
            <ul>
              <li>Cybard.design@gmail.com</li>
              <li><ContactPhoneLink href="tel:+15202303957">+1 (520) 230-3957</ContactPhoneLink></li>
              <li>Remote • Worldwide</li>
            </ul>
          </motion.div>
        </Grid>
      </Container>

      <Copy>© {new Date().getFullYear()} Cybard. All rights reserved.</Copy>
    </Footer>
  );
}
